import { useState } from 'react';

import { Text } from '@gympass/yoga';
import { useFlag } from '@unleash/proxy-client-react';
import { useIntl, FormattedMessage } from 'react-intl';
import { FeatureToggle } from 'utils/unleash/toggles';

import { NoteType } from 'components/NotesSection';

import { Modal } from '../../components';
import { useModals, usePermissionConfig } from '../../context';

import { AddOrViewNoteProps } from './types';

import * as S from './styles';

const AddOrViewNote: React.FC<AddOrViewNoteProps> = ({
  modalKey,
  props,
  isOpen,
  onClose,
}) => {
  const intl = useIntl();
  const { closeModal } = useModals();
  const { hasAccess } = usePermissionConfig();
  const {
    noteId,
    title,
    date,
    isView,
    noteType = NoteType.NOTES,
    description,
    author,
    onClickButton,
    onClickDelete,
  } = props;
  const [isViewState, setIsViewState] = useState(isView || false);
  const [titleState, setTitleState] = useState(title || '');
  const [noteState, setNoteState] = useState(description || '');

  const isNotesType = noteType === NoteType.NOTES;

  const isPatientRecordEnabled = useFlag(FeatureToggle.PatientRecords);

  const handleSubmit = () => {
    if (noteId && !isViewState && onClickButton) {
      onClickButton(titleState, noteState, noteId);
    } else if (!isViewState && onClickButton) {
      onClickButton(titleState, noteState);
    } else {
      setIsViewState(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      closeModal={() => closeModal(modalKey)}
      onAfterClose={() => {
        if (onClose) onClose();
      }}
    >
      <S.Container data-testid="addOrViewNoteModal">
        <Text.H5 data-testid="modalLabel">
          {!title?.trim() ? (
            <FormattedMessage
              id={
                isNotesType
                  ? 'NOTES.MODAL.ADD.LABEL'
                  : 'LOGBOOK.MODAL.ADD.LABEL'
              }
            />
          ) : (
            title
          )}
        </Text.H5>
        <S.Instructions>
          {!isViewState ? (
            <FormattedMessage
              id={
                isNotesType
                  ? 'NOTES.MODAL.ADD.INSTRUCTION'
                  : 'LOGBOOK.MODAL.ADD.INSTRUCTION'
              }
            />
          ) : (
            `${new Intl.DateTimeFormat(intl.locale, {
              dateStyle: 'short',
              timeStyle: 'medium',
            }).format(date)} | ${author}`
          )}
        </S.Instructions>
        <S.Body>
          <S.InputTitle
            cleanable={false}
            full
            disabled={isViewState}
            label=""
            placeholder={intl.formatMessage({
              id: isNotesType
                ? 'NOTES.MODAL.PLACE.TITLE'
                : 'LOGBOOK.MODAL.PLACE.TITLE',
            })}
            value={titleState}
            onChange={(e: React.FormEvent<HTMLInputElement>) => {
              setTitleState(e.currentTarget.value);
            }}
          />
          <S.InputMessage
            full
            disabled={isViewState}
            placeholder={intl.formatMessage({
              id: isNotesType
                ? 'NOTES.MODAL.PLACE.NOTE'
                : 'LOGBOOK.MODAL.PLACE.NOTE',
            })}
            value={noteState}
            onChange={(e: React.FormEvent<HTMLInputElement>) => {
              setNoteState(e.currentTarget.value);
            }}
          />
        </S.Body>
        <S.ActionsContent>
          {hasAccess(noteType) && !isPatientRecordEnabled && (
            <S.NotesButton
              data-testid="noteButton"
              type="button"
              onClick={handleSubmit}
            >
              {!isViewState && !onClickDelete && (
                <FormattedMessage
                  id={
                    isNotesType
                      ? 'NOTES.MODAL.BUTTON.SAVE'
                      : 'LOGBOOK.MODAL.BUTTON.SAVE'
                  }
                />
              )}
              {!isViewState && onClickDelete && (
                <FormattedMessage id="NOTES.MODAL.BUTTON.EDIT.SAVE" />
              )}
              {isViewState && onClickDelete && (
                <FormattedMessage
                  id={
                    isNotesType
                      ? 'NOTES.MODAL.BUTTON.EDIT'
                      : 'LOGBOOK.MODAL.BUTTON.EDIT'
                  }
                />
              )}
            </S.NotesButton>
          )}

          {onClickDelete && !isViewState && hasAccess(noteType) && (
            <S.NotesDeleteButton
              type="button"
              data-testid="deleteButton"
              onClick={() => {
                onClickDelete();
              }}
            >
              <FormattedMessage
                id={
                  isNotesType
                    ? 'NOTES.MODAL.BUTTON.DELETE'
                    : 'LOGBOOK.MODAL.BUTTON.DELETE'
                }
              />
            </S.NotesDeleteButton>
          )}
        </S.ActionsContent>
      </S.Container>
    </Modal>
  );
};

export default AddOrViewNote;
